import React, {
	SetStateAction,
	useCallback,
	useMemo,
	useState,
} from 'react';
import { CardTabListType } from 'antd/es/card/Card';
import {
	Row,
	Col,
	Flex,
} from 'antd';
import {
	useMutation,
	useQueryClient,
} from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import {
	useHistory,
	useLocation,
} from 'react-router';
import { splitActionKey } from '@shared/utils/splitActionKey';
import { capitalize } from '@shared/utils/string';
import { DemurrageTerms } from '@shared/utils/constants';
import type {
	CargoPortWithTurnDays,
	CargoWithPortsAndCalculationsJSON,
} from '@api/features/laytime/getCargosAndLaytimes';
import Card from '@client/components/Card/Card';
import {
	calculateLaytime,
	generateLaytimePdf,
} from '@client/lib/api';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import Button from '@client/components/Button';
import { useVoyage } from '@client/screens/fleet/VoyageDetailsScreen/components/VoyageProvider/VoyageProvider';
import CountryFlag from '@client/components/CountryFlag';
import CreditNoteCheckbox from '@client/components/CreditNoteCheckbox';
import showErrorNotification from '@client/utils/showErrorNotification';
import Exclusions from '../Exclusions/Exclusions';
import KeyStats from '../KeyStats';
import LaytimeActivities from '../LaytimeActivities';
import Timesheets from '../Timesheet/Timesheets';

type Props = {
	selectedCargo?: CargoWithPortsAndCalculationsJSON;
	portCallId?: number;
	enableNavigation?: boolean;
	enableExport?: boolean;
	selectedCargoPortId?: string | undefined;
	setSelectedCargoPortId?: React.Dispatch<SetStateAction<string | undefined>>;
	cargoPorts?: CargoPortWithTurnDays[] | null;
	tabs?: CardTabListType[];
	selectedTab?: string | undefined;
	onTabChange?: (tab: string) => void;
};

const getCargoPortTabs = (
	cargo: CargoWithPortsAndCalculationsJSON
): CardTabListType[] | undefined => {
	const { CargoPorts } = cargo;

	return CargoPorts
		.map((cp) => {
			const { action } = splitActionKey(cp.portAndActionKey);

			return ({
				id: cp.id.toString(),
				key: `${cp.id.toString()} - ${action}`,
				label: (
					<Flex gap={5}>
						<CountryFlag countryCode={cp.port.countryCode} />
						<p style={{ margin: 0, color: '#1890ff' }}>
							{`${cp.port.name} - ${capitalize(action)}`}
						</p>
					</Flex>
				),
			});
		});
};

const Claims = ({
	selectedCargo: propSelectedCargo,
	portCallId,
	enableNavigation = false,
	enableExport = false,
	selectedCargoPortId,
	setSelectedCargoPortId,
	cargoPorts: propCargoPorts,
	tabs: propTabs,
	selectedTab: propSelectedTab,
	onTabChange,
}: Props) => {
	const [excludeTimesheet, setExcludeTimesheet] = useState(false);
	const queryClient = useQueryClient();
	const history = useHistory();
	const location = useLocation();
	const { cargos } = useVoyage();
	const [exclusionsChanged, setExclusionsChanged] = useState(false);

	const searchParams = useMemo(() => {
		const hashQuery = location.hash.split('?')[1];

		return new URLSearchParams(hashQuery);
	}, [location.hash]);

	const selectedCargoId = searchParams.get('cargoId');

	const cargoPorts = useMemo(() => {
		if (propSelectedCargo) {
			return propSelectedCargo.CargoPorts;
		}

		if (propCargoPorts) {
			return propCargoPorts;
		}

		if (!cargos || portCallId === undefined) {
			return null;
		}

		const allCargoPorts = cargos.flatMap((c) => c.CargoPorts);

		return allCargoPorts.filter((cp) => cp.portCallId === portCallId);
	}, [propSelectedCargo, propCargoPorts, cargos, portCallId]);

	const selectedTab = useMemo(() => {
		if (propSelectedTab) {
			return propSelectedTab;
		}

		if (!selectedCargoPortId) {
			return undefined;
		}

		const cp = cargoPorts?.find((c) => c.id.toString() === selectedCargoPortId);

		if (cp) {
			const { action } = splitActionKey(cp.portAndActionKey);

			return `${cp.id.toString()} - ${action}`;
		}

		return selectedCargoPortId?.toString();
	}, [cargoPorts, propSelectedTab, selectedCargoPortId]);

	const selectedCargo = useMemo(() => {
		if (propSelectedCargo) {
			return propSelectedCargo;
		}

		if (!cargos || portCallId === undefined) {
			return null;
		}

		if (selectedCargoId) {
			const cargo = cargos.find((c) => c.id === Number(selectedCargoId));

			if (cargo) {
				return cargo;
			}
		}

		const allCargoPorts = cargos.flatMap((c) => c.CargoPorts);
		const portCallCargoPorts = allCargoPorts.filter((cp) => cp.portCallId === portCallId);
		const relevantCargos = cargos.filter(
			(c) => portCallCargoPorts.some((cp) => cp.cargoId === c.id),
		);

		return relevantCargos[0];
	}, [propSelectedCargo, cargos, portCallId, selectedCargoId]);

	const tabs = useMemo(() => {
		return propTabs || (propSelectedCargo ? getCargoPortTabs(selectedCargo!) : []);
	}, [propTabs, propSelectedCargo, selectedCargo]);

	const handleTabChange = useCallback((tab: string) => {
		const cargoPortId = tab.split(' ')[0];

		if (!cargoPortId) {
			return;
		}

		onTabChange?.(tab);
		setSelectedCargoPortId?.(cargoPortId);

		if (!enableNavigation || !setSelectedCargoPortId || !selectedCargo) {
			return;
		}

		const path = location.pathname;
		const newPath = `${path}#/claims?cargoId=${selectedCargo.id}&cargoPortId=${cargoPortId}`;
		history.replace(newPath);
	}, [
		enableNavigation,
		setSelectedCargoPortId,
		selectedCargo,
		location.pathname,
		history,
		onTabChange,
	]);

	const { makeBlockable } = useNavigationBlock();
	const tabChangeWithNavigationBlock = enableNavigation ?
		makeBlockable(handleTabChange) :
		handleTabChange;

	const exportToPdfMutation = useMutation(
		generateLaytimePdf,
		{
			onSuccess: (data) => {
				const url = window.URL.createObjectURL(data);
				const link = document.createElement('a');
				link.href = url;

				const selectedPortId = selectedCargoPortId ?
					Number(selectedCargoPortId) :
					undefined;

				link.download = `laytime-report-${selectedPortId}.pdf`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			},
			onError: (error) => {
				showErrorNotification('Could not export laytime PDF', error as Error);
			},
		},
	);

	const handleExport = useCallback(async () => {
		if (!enableExport || !selectedCargo) {
			return;
		}

		const selectedPortId = selectedCargoPortId ?
			Number(selectedCargoPortId) :
			undefined;

		exportToPdfMutation.mutate({
			cargoId: selectedCargo.id,
			selectedCargoPortId: selectedPortId,
			excludeTimesheet,
		});
	}, [
		enableExport,
		excludeTimesheet,
		exportToPdfMutation,
		selectedCargo,
		selectedCargoPortId,
	]);

	const calculateMutation = useMutation(
		calculateLaytime,
		{
			onSuccess: () => {
				queryClient.invalidateQueries('cargos');
				queryClient.invalidateQueries('exclusionItems');
				queryClient.invalidateQueries('voyageDetails');
				queryClient.invalidateQueries('voyageInvoices');
			},
		},
	);

	const exportBtn = useMemo(() => {
		if (!enableExport) {
			return <></>;
		}

		return (
			<Button
				icon={(<FontAwesomeIcon icon={faFilePdf} />)}
				type="primary"
				onClick={handleExport}
				loading={calculateMutation.isLoading || exportToPdfMutation.isLoading}
				confirmTitle={(
					<CreditNoteCheckbox
						description={'Exclude timesheet'}
						createCreditNote={excludeTimesheet}
						setCreateCreditNote={setExcludeTimesheet}
						showCumulativeAlert={false}
						showTooltip={false}
					/>
				)}
			>
				Export to PDF
			</Button>
		);
	}, [
		calculateMutation.isLoading,
		enableExport,
		excludeTimesheet,
		exportToPdfMutation.isLoading,
		handleExport,
	]);

	const selectedCargoPort = useMemo(() => {
		if (!selectedCargo) {
			return null;
		}

		if (selectedCargoPortId) {
			return selectedCargo.CargoPorts.find(
				(cp) => Number(cp.id) === Number(selectedCargoPortId),
			);
		}

		if (propSelectedCargo) {
			const tab = selectedCargo?.CargoPorts
				.find((cp) => selectedTab?.includes(cp.id.toString()));

			if (tab) {
				return tab;
			}

			return selectedCargo?.CargoPorts[0];
		}

		if (cargoPorts) {
			return cargoPorts[0];
		}

		return null;
	}, [selectedCargo, selectedCargoPortId, propSelectedCargo, cargoPorts, cargos, selectedTab]);

	const handleCalculateLaytime = useCallback(async () => {
		if (!selectedCargo) {
			return;
		}

		await calculateMutation.mutateAsync(selectedCargo.id);
	}, [selectedCargo, calculateMutation]);

	if (!selectedCargo || !selectedCargoPort) {
		return null;
	}

	return (
		<Card
			tabList={tabs}
			activeTabKey={selectedTab}
			onTabChange={tabChangeWithNavigationBlock}
			tabBarExtraContent={exportBtn}
		>
			<Row gutter={[16, 16]}>
				<Col span={8}>
					<Flex vertical gap={15}>
						<KeyStats
							cargo={selectedCargo}
							selectedCargoPort={selectedCargoPort}
							handleCalculateLaytime={handleCalculateLaytime}
							exclusionsChanged={exclusionsChanged}
							setExclusionsChanged={setExclusionsChanged}
						/>
						<LaytimeActivities
							selectedCargoPort={selectedCargoPort}
						/>
					</Flex>
				</Col>
				<Col span={16}>
					<Flex vertical gap={10}>
						<Exclusions 
							demurrageTerm={selectedCargo?.demurrageTerm ?? DemurrageTerms.DEMURRAGE}
							selectedCargoPort={selectedCargoPort} 
							setExclusionsChanged={setExclusionsChanged}
						/>
						<Timesheets
							selectedCargo={selectedCargo}
							selectedCargoPort={selectedCargoPort}
							isLoading={calculateMutation.isLoading}
						/>
					</Flex>
				</Col>
			</Row>
		</Card>
	);
};

export default Claims;
